import { Check, Search, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { projectService } from '../../services/projectService';
import { User } from '../../types';

interface ProjectMemberSelectorProps {
  projectId: string;
  selectedMembers: string[];
  onChange: (memberIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
}

const ProjectMemberSelector: React.FC<ProjectMemberSelectorProps> = ({
  projectId,
  selectedMembers,
  onChange,
  disabled = false,
  placeholder = 'Select team members...',
  label = 'Project Members',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  // Load project members
  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      try {
        const response = await projectService.getProjectTeam(projectId);
        if (response.success && response.data?.data) {
          setMembers(response.data.data);
        }
      } catch (error) {
        console.error('Failed to load project members:', error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadMembers();
    }
  }, [projectId]);

  // Filter members based on search term
  const filteredMembers = members.filter(
    member =>
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected member objects
  const selectedMemberObjects = members.filter(member => selectedMembers.includes(member.id || ''));

  const handleMemberToggle = (memberId: string) => {
    if (selectedMembers.includes(memberId)) {
      onChange(selectedMembers.filter(id => id !== memberId));
    } else {
      onChange([...selectedMembers, memberId]);
    }
  };

  const removeMember = (memberId: string) => {
    onChange(selectedMembers.filter(id => id !== memberId));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      {/* Selected members display */}
      {selectedMemberObjects.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedMemberObjects.map(member => (
            <span
              key={member.id}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800"
            >
              {member.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeMember(member.id || '')}
                  className="ml-1 text-brand-primary hover:text-brand-primary-hover"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          {!disabled && selectedMemberObjects.length > 1 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Dropdown trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary bg-white text-left disabled:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-sm ${selectedMembers.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}
            >
              {selectedMembers.length === 0
                ? placeholder
                : `${selectedMembers.length} member${selectedMembers.length === 1 ? '' : 's'} selected`}
            </span>
            <svg
              className="h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {/* Dropdown menu */}
        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
            {/* Search input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full h-9 pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-sm transition-colors"
                />
              </div>
            </div>

            {/* Members list */}
            <div className="max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading members...</div>
              ) : filteredMembers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? 'No members found' : 'No team members available'}
                </div>
              ) : (
                filteredMembers.map(member => {
                  const memberId = member.id || '';
                  const isSelected = selectedMembers.includes(memberId);

                  return (
                    <button
                      key={memberId}
                      type="button"
                      onClick={() => handleMemberToggle(memberId)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                        isSelected ? 'bg-brand-50' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {member.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.name}
                          </p>
                          <p className="text-sm text-gray-500 truncate">{member.email}</p>
                          {member.role && (
                            <p className="text-xs text-gray-400 capitalize">{member.role}</p>
                          )}
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-brand-primary" />}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {selectedMembers.length} of {members.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="text-brand-primary hover:text-brand-primary-hover"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {isOpen && <div className="fixed inset-0 z-5" onClick={() => setIsOpen(false)} />}
    </div>
  );
};

export default ProjectMemberSelector;
